import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { LeaderboardOut } from '../api/types';
import { formatMs } from '../components/Timer';
import { useOnline } from '../hooks/useOnline';
import { formatSeen, lastSeen, markSeen } from '../lib/lastSeen';

const SEEN_KEY = 'leaderboard';

export function Leaderboard() {
  const [data, setData] = useState<LeaderboardOut | null>(null);
  const [error, setError] = useState(false);
  const online = useOnline();

  useEffect(() => {
    let cancelled = false;
    api<LeaderboardOut>('/leaderboard')
      .then((d) => {
        if (cancelled) return;
        markSeen(SEEN_KEY);
        setData(d);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="notice">
        {online
          ? 'The leaderboard needs a connection.'
          : 'You’re offline — the leaderboard isn’t downloaded yet.'}
      </p>
    );
  }
  if (!data) return <p className="notice">Loading leaderboard…</p>;

  return (
    <section className="leaderboard">
      <h1>Leaderboard</h1>
      {!online && (
        <p className="notice offline-note">
          Offline — showing the last update from {formatSeen(lastSeen(SEEN_KEY))}.
        </p>
      )}
      <p className="sub">
        {data.date}
        {data.my_rank ? ` · you: #${data.my_rank}` : ''}
      </p>
      {data.entries.length === 0 ? (
        <p className="notice">No one has finished today’s puzzle yet. Be the first.</p>
      ) : (
        <table className="ranks">
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              <th>Points</th>
              <th>Level</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={`${e.rank}-${e.name}`} className={e.is_me ? 'me' : ''}>
                <td>{e.rank}</td>
                <td>{e.name}</td>
                <td>{e.points}</td>
                <td className="diff-tag">{e.difficulty}</td>
                <td>{formatMs(e.time_ms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
